import React, { useState, useMemo } from 'react';
import gamesData from '../games.json';
import slotsData from '../slots.json';
import GameGrid from '../components/GameGrid';
import SearchBar from '../components/SearchBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GameModal from '../components/GameModal';
import CookieConsent from '../components/CookieConsent';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';

function HomePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);

  // Merge games and slots data
  const allGames = useMemo(() => {
    const slots = Array.isArray(slotsData) ? slotsData : [];
    return [...gamesData, ...slots];
  }, []);

  // Get active games (not disabled)
  const activeGames = useMemo(() => {
    return allGames.filter(game => !game.disabled);
  }, [allGames]);

  // Extract unique game types
  const availableGameTypes = useMemo(() => {
    const types = new Set();
    activeGames.forEach(game => {
      if (game.gameType) types.add(game.gameType);
      if (game.gameTypeCategory) types.add(game.gameTypeCategory);
    });
    return Array.from(types).sort();
  }, [activeGames]);

  // Extract unique themes
  const availableThemes = useMemo(() => {
    const themes = new Set();
    activeGames.forEach(game => {
      if (game.themes && Array.isArray(game.themes)) {
        game.themes.forEach(theme => {
          if (theme.label) themes.add(theme.label);
        });
      }
    });
    return Array.from(themes).sort();
  }, [activeGames]);

  // Filter and sort games
  const filteredGames = useMemo(() => {
    let filtered = activeGames.filter(game => {
      const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGameType = !gameTypeFilter || 
        game.gameType === gameTypeFilter || 
        game.gameTypeCategory === gameTypeFilter;
      const matchesTheme = !themeFilter || 
        (game.themes && Array.isArray(game.themes) && 
         game.themes.some(theme => theme.label === themeFilter));
      
      return matchesSearch && matchesGameType && matchesTheme;
    });

    if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => b.name.localeCompare(a.name));
    }

    return filtered;
  }, [activeGames, searchTerm, sortBy, gameTypeFilter, themeFilter]);

  const handleGamePlay = (game) => {
    setSelectedGame(game);
  };

  const handleCloseModal = () => {
    setSelectedGame(null);
  };

  // Dynamic SEO based on filters
  const seoTitle = useMemo(() => {
    if (gameTypeFilter) {
      return `Free ${gameTypeFilter} Games - Play Online at iGames.ng`;
    }
    if (searchTerm) {
      return `Search: ${searchTerm} - Free Casino Games at iGames.ng`;
    }
    return "iGames.ng - Play 3000+ Free Casino Games Online | No Download Required";
  }, [gameTypeFilter, searchTerm]);

  const seoDescription = useMemo(() => {
    if (gameTypeFilter) {
      return `Play free ${gameTypeFilter} games online. Browse ${filteredGames.length}+ games with no download or registration required at iGames.ng`;
    }
    return `Play 3000+ free casino games online! Enjoy slots, video poker, table games & more. No download, no registration required. Start playing now at iGames.ng`;
  }, [gameTypeFilter, filteredGames.length]);

  return (
    <div className="App">
      <SEO 
        title={seoTitle}
        description={seoDescription}
      />
      <Header />
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortBy={sortBy}
        setSortBy={setSortBy}
        totalGames={filteredGames.length}
        gameTypeFilter={gameTypeFilter}
        setGameTypeFilter={setGameTypeFilter}
        themeFilter={themeFilter}
        setThemeFilter={setThemeFilter}
        availableGameTypes={availableGameTypes}
        availableThemes={availableThemes}
      />
      <main>
        <GameGrid games={filteredGames} onGamePlay={handleGamePlay} />
      </main>
      <Footer />
      <ScrollToTop />
      {selectedGame && (
        <GameModal game={selectedGame} onClose={handleCloseModal} />
      )}
      <CookieConsent />
    </div>
  );
}

export default HomePage;
